import { groupBy } from 'lodash-es';
import type { Department } from 'maestro-shared/referential/Department';
import { DepartmentSort } from 'maestro-shared/referential/Department';
import {
  type Region,
  RegionList,
  Regions
} from 'maestro-shared/referential/Region';
import {
  type DisplayStatusResult,
  isCampaignLaunchable
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanDisplayStatus';
import {
  hasCompanyEchelon,
  hasDepartmentalEchelon
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanEchelon';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import { useContext, useMemo } from 'react';
import { useAuthentication } from '../../../hooks/useAuthentication';
import { ApiClientContext } from '../../../services/apiClient';
import { findPrescriptionPlan } from '../prescriptionPlan';
import {
  type AggregateDisplayStatus,
  buildAggregateDisplayStatus,
  buildEchelonDisplayStatus
} from './ProgrammingPlanTrackingTable.utils';

export const sortedDepartments = (region: Region) =>
  [...Regions[region].departments].sort(DepartmentSort);

interface PlanStatusInfo {
  nationalDisplayStatus: DisplayStatusResult;
  regionalDisplayStatus: DisplayStatusResult | undefined;
  departmentalDisplayStatus: DisplayStatusResult | undefined;
  isEligible: boolean;
  isLaunchable: boolean;
  regionalAggregate: AggregateDisplayStatus;
  departmentalAggregate: AggregateDisplayStatus | undefined;
}

export const useProgrammingPlanTrackingStatus = (
  programmingPlans: ProgrammingPlanChecked[],
  region?: Region,
  department?: Department
) => {
  const apiClient = useContext(ApiClientContext);
  const { hasRole, hasUserPermission } = useAuthentication();
  const viewerOwnsNationalRow = hasRole(
    'NationalCoordinator',
    'NationalObserver'
  );

  const planIds = useMemo(
    () => programmingPlans.map((plan) => plan.id),
    [programmingPlans]
  );

  const { data: prescriptions } = apiClient.useFindPrescriptionsQuery(
    { programmingPlanIds: planIds },
    { skip: planIds.length === 0 }
  );
  const hasCompanyEchelonPlan = programmingPlans.some((plan) =>
    hasCompanyEchelon(plan.distributionKind)
  );

  const { data: localPrescriptions } = apiClient.useFindLocalPrescriptionsQuery(
    {
      programmingPlanIds: planIds,
      allLevels: true,
      includeCompanies: hasCompanyEchelonPlan,
      includes: ['laboratories' as const]
    },
    { skip: planIds.length === 0 }
  );

  const prescriptionsByPlan = useMemo(
    () =>
      groupBy(
        prescriptions ?? [],
        (prescription) =>
          findPrescriptionPlan(programmingPlans, prescription)?.id
      ),
    [prescriptions, programmingPlans]
  );
  const localPrescriptionsByPrescription = useMemo(
    () => groupBy(localPrescriptions ?? [], 'prescriptionId'),
    [localPrescriptions]
  );

  const planStatusInfo = useMemo(() => {
    const map = new Map<string, PlanStatusInfo>();
    for (const plan of programmingPlans) {
      const planPrescriptions = prescriptionsByPlan[plan.id] ?? [];
      const planLocalPrescriptions = planPrescriptions.flatMap(
        (_) => localPrescriptionsByPrescription[_.id] ?? []
      );
      const nationalDisplayStatus = buildEchelonDisplayStatus(
        plan,
        planPrescriptions,
        planLocalPrescriptions,
        'National',
        undefined,
        undefined,
        viewerOwnsNationalRow
      );

      const regionalDisplayStatus = region
        ? buildEchelonDisplayStatus(
            plan,
            planPrescriptions,
            planLocalPrescriptions,
            'Regional',
            region
          )
        : undefined;

      const regionalAggregate = buildAggregateDisplayStatus(
        (region ? [region] : RegionList).map((regionColumn) =>
          buildEchelonDisplayStatus(
            plan,
            planPrescriptions,
            planLocalPrescriptions,
            'Regional',
            regionColumn
          )
        )
      );

      const departmentalDisplayStatus =
        department && hasDepartmentalEchelon(plan.distributionKind)
          ? buildEchelonDisplayStatus(
              plan,
              planPrescriptions,
              planLocalPrescriptions,
              'Departmental',
              region,
              department
            )
          : undefined;

      const departmentalAggregate =
        !department && hasDepartmentalEchelon(plan.distributionKind)
          ? buildAggregateDisplayStatus(
              (region ? [region] : RegionList).flatMap((regionColumn) =>
                sortedDepartments(regionColumn).map((departmentColumn) =>
                  buildEchelonDisplayStatus(
                    plan,
                    planPrescriptions,
                    planLocalPrescriptions,
                    'Departmental',
                    regionColumn,
                    departmentColumn
                  )
                )
              )
            )
          : undefined;

      const isSubmittedToAdmin =
        plan.nationalStatus.status === 'SubmittedToAdmin';
      const isEligible = department
        ? departmentalDisplayStatus?.value === 'ReadyToSend'
        : region
          ? regionalDisplayStatus?.value === 'ReadyToSend'
          : hasRole('AdministratorBGIR')
            ? isSubmittedToAdmin
            : nationalDisplayStatus.value === 'ReadyToSend';

      const isLaunchable =
        hasUserPermission('launchProgrammingPlanCampaign') &&
        isCampaignLaunchable(plan) &&
        nationalDisplayStatus.value !== 'NotApplicable';

      map.set(plan.id, {
        nationalDisplayStatus,
        regionalDisplayStatus,
        departmentalDisplayStatus,
        isEligible,
        isLaunchable,
        regionalAggregate,
        departmentalAggregate
      });
    }
    return map;
  }, [
    programmingPlans,
    prescriptionsByPlan,
    localPrescriptionsByPrescription,
    hasRole,
    hasUserPermission,
    region,
    department,
    viewerOwnsNationalRow
  ]);

  const readyToSendPlans = useMemo(
    () =>
      programmingPlans.filter(
        (plan) => planStatusInfo.get(plan.id)?.isEligible
      ),
    [programmingPlans, planStatusInfo]
  );

  return {
    planStatusInfo,
    readyToSendPlans,
    prescriptionsByPlan,
    localPrescriptionsByPrescription
  };
};
